import React from "react";
import { TextField, IconPicker } from "@/pages/admin/Fields";

/** Editor for an array-of-objects "list" field with add/edit/delete.
 *  Each item is rendered via the provided ItemEditor renderer.
 */
export default function ListEditor({ label, items, onChange, newItem, ItemEditor, testidBase }) {
  const list = Array.isArray(items) ? items : [];
  const update = (i, next) => {
    const arr = [...list]; arr[i] = next; onChange(arr);
  };
  const remove = (i) => onChange(list.filter((_, idx) => idx !== i));
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= list.length) return;
    const arr = [...list];
    [arr[i], arr[j]] = [arr[j], arr[i]];
    onChange(arr);
  };
  const add = () => onChange([...list, typeof newItem === "function" ? newItem() : { ...newItem }]);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-extrabold text-navy uppercase tracking-wider">{label} <span className="text-slate-400 font-normal">({list.length})</span></h4>
        <button
          type="button"
          onClick={add}
          className="text-xs font-bold text-white bg-navy hover:bg-navy-deep px-3 py-1.5 rounded-lg"
          data-testid={`${testidBase}-add`}
        >
          + Add
        </button>
      </div>
      <div className="space-y-3">
        {list.map((item, i) => (
          <div key={i} className="border border-slate-200 rounded-2xl p-4 bg-slate-50" data-testid={`${testidBase}-item-${i}`}>
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs uppercase tracking-wider font-bold text-slate-500">#{i + 1}</span>
              <div className="flex gap-1.5">
                <button type="button" onClick={() => move(i, -1)} className="text-xs px-2 py-1 rounded bg-white border border-slate-200 hover:border-navy" data-testid={`${testidBase}-up-${i}`}>↑</button>
                <button type="button" onClick={() => move(i, 1)} className="text-xs px-2 py-1 rounded bg-white border border-slate-200 hover:border-navy" data-testid={`${testidBase}-down-${i}`}>↓</button>
                <button type="button" onClick={() => remove(i)} className="text-xs px-2 py-1 rounded bg-red-50 text-red-600 border border-red-200 hover:bg-red-100" data-testid={`${testidBase}-remove-${i}`}>Delete</button>
              </div>
            </div>
            <ItemRow item={item} onChange={(next) => update(i, next)} testidBase={`${testidBase}-${i}`} render={ItemEditor} />
          </div>
        ))}
        {list.length === 0 && (
          <div className="text-center text-sm text-slate-400 italic py-6 border border-dashed border-slate-200 rounded-2xl">
            No items yet. Click "+ Add" to create one.
          </div>
        )}
      </div>
    </div>
  );
}

/** Stable wrapper: keeps a single component identity across renders, calling
 *  the user-provided render prop with the current item. This prevents React from
 *  unmounting/remounting form inputs on every keystroke. */
function ItemRow({ item, onChange, testidBase, render }) {
  return render({ item, onChange, testidBase });
}
